import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';

/**
 * The "Discard unsaved changes?" confirmation, shared by the sidebar's save
 * footer and the workspace tab strip's close guard (TGCLOUD-1839), so the two
 * prompts about the same unsaved edits cannot drift apart.
 *
 * House style: the painted DialogTitle with `pb: 1`, the `pt: 16px !important`
 * that stops MUI collapsing the body into the title, the SAFE action on the
 * left with the focus, and the destructive one on the right as contained
 * error.
 *
 * `dense` is for a prompt read inside a panel (body2 text and small buttons,
 * which is what the save footer has always used). A prompt raised from the
 * page chrome, like closing a tab, is read next to the canvas and takes the
 * default sizes.
 *
 * Props:
 *   open          whether the dialog is shown
 *   onCancel      keep the changes (the default action; Escape and a backdrop
 *                 click do this too)
 *   onConfirm     discard them
 *   cancelLabel   defaults to "Cancel"
 *   confirmLabel  defaults to "Discard"
 *   dense         body2 + small buttons
 *   children      the sentence saying what will be lost
 */
export default function DiscardChangesDialog({
  open,
  onCancel,
  onConfirm,
  title = 'Discard unsaved changes?',
  cancelLabel = 'Cancel',
  confirmLabel = 'Discard',
  ariaLabel = 'Confirm discard',
  dense = false,
  children
}) {
  const buttonSize = dense ? 'small' : undefined;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      role="alertdialog"
      aria-label={ariaLabel}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {/* A paragraph, so `children` must be phrasing content. */}
        <Typography
          variant={dense ? 'body2' : 'body1'}
          sx={{
            color: 'text.primary'
          }}
        >
          {children}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="outlined"
          size={buttonSize}
          autoFocus
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          size={buttonSize}
          color="error"
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * "Person and Diseases" — how the prompts join dirty section labels.
 */
export const joinSectionLabels = (labels = []) => labels.join(' and ');

/**
 * What a prompt says about unsaved edits spread over open TABS: one tab is
 * named with its sections, several are listed. Shared by the tab strip's close
 * guard and the app bar's Log Out / user-group switcher, so a clinician is told
 * the same thing whichever of them is about to discard the work. Phrasing
 * content only, because DiscardChangesDialog puts it in a paragraph.
 *
 * `sessions` are workspace sessions (or the same shape): { sessionId, title,
 * unsavedSections }.
 */
export function UnsavedTabsSummary({ sessions = [] }) {
  if (sessions.length === 1) {
    return (
      <>
        Unsaved changes to{' '}
        <strong>{joinSectionLabels(sessions[0].unsavedSections)}</strong> in{' '}
        <strong>{sessions[0].title}</strong> will be lost.
      </>
    );
  }
  if (sessions.length > 1) {
    return (
      <>
        Unsaved changes in {sessions.length} tabs will be lost:{' '}
        {sessions.map((s, i) => (
          <React.Fragment key={s.sessionId}>
            {i > 0 ? ', ' : ''}
            <strong>{s.title}</strong> ({joinSectionLabels(s.unsavedSections)})
          </React.Fragment>
        ))}
        .
      </>
    );
  }
  return 'All unsaved changes will be lost.';
}
